import {
  computed,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved,
  reaction,
  runInAction,
} from 'mobx'

import {
  DefaultError,
  QueryClient,
  QueryKey,
  QueryObserver,
  QueryObserverOptions,
  QueryObserverResult,
} from '@tanstack/react-query'

import { queryClient as globalQueryClient } from '../query-client'

interface ObservableQueryConfig {
  queryClient?: QueryClient
}

/**
 * MobX-compatible wrapper for react-query queries. Use it inside MobX stores
 * to fetch and cache data with automatic deduplication, loading/error states,
 * and reactive refetching.
 *
 * The options function is reactive — if it references MobX observables,
 * the query automatically refetches when those values change.
 *
 * @example
 * // Basic query
 * private _usersQuery = new ObservableQuery(() => ({
 *   queryKey: ['users'],
 *   queryFn: () => fetchUsers(),
 * }))
 *
 * @example
 * // Query with reactive filters (refetches when filters change)
 * private _ordersQuery = new ObservableQuery(() => ({
 *   queryKey: ['orders', filtersStore.allFilters],
 *   queryFn: () => fetchOrders(filtersStore.allFilters),
 * }))
 *
 * @example
 * // Conditional query (disabled until id is set)
 * private _productQuery = new ObservableQuery(() => ({
 *   queryKey: ['product', this.productId],
 *   queryFn: () => fetchProduct(this.productId!),
 *   enabled: !!this.productId,
 * }))
 *
 * @example
 * // Reading query state
 * get users() { return this._usersQuery.data ?? [] }
 * get isLoading() { return this._usersQuery.isLoading }
 */
export class ObservableQuery<
  TData = unknown,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
> {
  private _queryResult: QueryObserverResult<TData, TError> | null = null
  private _queryClient: QueryClient

  constructor(
    private _getOptions: () => QueryObserverOptions<TData, TError, TData, TData, TQueryKey>,
    config?: ObservableQueryConfig,
  ) {
    this._queryClient = config?.queryClient ?? globalQueryClient

    makeObservable<ObservableQuery<TData, TError, TQueryKey>, '_queryResult'>(this, {
      _queryResult: observable.ref,
      data: computed,
      dataUpdatedAt: computed,
      isLoading: computed,
      isPlaceholderData: computed,
      error: computed,
    })

    let subscribeDispose: (() => void) | undefined

    onBecomeObserved(this, '_queryResult', () => (subscribeDispose = this._subscribe()))
    onBecomeUnobserved(this, '_queryResult', () => subscribeDispose?.())
  }

  get data() {
    return this._queryResult?.data
  }

  /** When the current data was fetched, in ms; 0 before the first load. */
  get dataUpdatedAt() {
    return this._queryResult?.dataUpdatedAt ?? 0
  }

  /** True when no data is available yet (initial fetch). */
  get isLoading() {
    return this._queryResult?.isLoading ?? true
  }

  /** True whenever a fetch is in progress, including the initial fetch and background refetches when stale data already exists. */
  get isRefreshing() {
    return this._queryResult?.isFetching ?? true
  }

  /** True while the query shows `placeholderData` instead of its own result. */
  get isPlaceholderData() {
    return this._queryResult?.isPlaceholderData ?? false
  }

  get error() {
    return this._queryResult?.error
  }

  invalidate() {
    return this._queryClient.invalidateQueries({ queryKey: this._getOptions().queryKey })
  }

  private _subscribe() {
    const observer = new QueryObserver<TData, TError, TData, TData, TQueryKey>(
      this._queryClient,
      this._getOptions(),
    )

    const observerDispose = observer.subscribe(result => {
      runInAction(() => (this._queryResult = result))
    })

    const reactionDispose = reaction(this._getOptions, newOptions => {
      observer.setOptions(newOptions)
    })

    return () => {
      observerDispose()
      reactionDispose()
      observer.destroy()
    }
  }
}
